import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { getPolyglot } from '../../../i18n';
import StoerkIdAPI from '../../../redux/stoerkID/StoerkIdAPI';
import useParseErrorMessage from '../../../handlingErrors/useParseErrorMessage';
import CustomersTable from './CustomersTable';
import { useAppDispatch, useAppSelector } from '../../../redux/store.model';
import { getAccountsByWorkspace } from '../../../redux/stoerkID/selectors/StoerkId.selectors';
import CustomersFilters, { CustomersFiltersProps } from './CustomersFilters';
import getAllTextFromRequest from '../util/getAllTextFromRequest';
import UserAPI from '../../../redux/users/api/UserAPI';

function CustomersListPage() {
  const polyglot = getPolyglot();
  const {
    error: errorSalesTeamRequests,
    isLoading: isLoadingSalesTeamRequests,
    isError: isErrorSalesTeamRequests,
  } = StoerkIdAPI.useGetSalesTeamRequestsQuery(undefined);
  const {
    error: errorWorkspaces,
    isLoading: isLoadingWorkspaces,
    isError: isErrorWorkspaces,
  } = StoerkIdAPI.useGetAllWorkspaceQuery(undefined);
  const dispatch = useAppDispatch();

  const error = errorSalesTeamRequests || errorWorkspaces;
  const isError = isErrorSalesTeamRequests || isErrorWorkspaces;
  const isLoading = isLoadingSalesTeamRequests || isLoadingWorkspaces;

  const parseErrorMessage = useParseErrorMessage();

  const groupedRequestByWorkspace = useAppSelector(getAccountsByWorkspace);

  // Derive stable string keys for dispatching (won't change reference on unrelated store updates)
  const workspaceIds = useAppSelector((state) => {
    const grouped = getAccountsByWorkspace(state);
    return (
      grouped
        ?.map((r) => r.workspace?.id)
        .filter(Boolean)
        .sort()
        .join(',') ?? ''
    );
  });
  const userIdsToFetch = useAppSelector((state) => {
    const grouped = getAccountsByWorkspace(state);
    return (
      grouped
        ?.flatMap((r) => [
          r.workspace?.accountHolderId,
          r.lastRequest?.creatorId,
        ])
        .filter(Boolean)
        .sort()
        .join(',') ?? ''
    );
  });

  // Derive stable ID keys for workspaces that need descendants fetched
  // Only fetch when childIds is null (meaning the backend doesn't provide it inline)
  const workspaceIdsNeedingDescendants = useAppSelector((state) => {
    const grouped = getAccountsByWorkspace(state);
    return (
      grouped
        ?.filter(
          (r) =>
            r.workspace &&
            (r.workspace.childIds === null || r.workspace.childIds.length > 0)
        )
        .map((r) => r.workspace!.id)
        .sort()
        .join(',') ?? ''
    );
  });

  // Dispatch usage/subworkspaces queries using stable ID keys
  useEffect(() => {
    if (!workspaceIds) return;
    workspaceIds.split(',').forEach((id) => {
      dispatch(
        StoerkIdAPI.endpoints.getWorkspaceUsage.initiate({
          workspaceId: id,
          includeChildWorkspaces: true,
        })
      );
    });
  }, [dispatch, workspaceIds]);

  // Only fetch descendants for workspaces where childIds is null
  useEffect(() => {
    if (!workspaceIdsNeedingDescendants) return;
    workspaceIdsNeedingDescendants.split(',').forEach((id) => {
      dispatch(StoerkIdAPI.endpoints.getWorkspaceDescendants.initiate(id));
    });
  }, [dispatch, workspaceIdsNeedingDescendants]);

  // Dispatch stores usage queries for workspaces that have subworkspaces or flexeserveUI.hierarchy setting
  const storesWorkspaceIds = useAppSelector((state) => {
    if (!groupedRequestByWorkspace) return '';
    return groupedRequestByWorkspace
      .filter((request) => {
        const workspaceId = request.workspace?.id;
        if (!workspaceId) return false;
        const childIds = request.workspace?.childIds;
        // Use childIds from workspace when available, fall back to descendants
        const hasChildren =
          childIds !== null && childIds !== undefined
            ? childIds.length > 0
            : (
                StoerkIdAPI.endpoints.getWorkspaceDescendants.select(
                  workspaceId
                )(state).data ?? []
              ).length > 1;
        const hasHierarchySetting =
          request.workspace?.settings?.['flexeserveUI.hierarchy'];
        return hasChildren || hasHierarchySetting;
      })
      .map((r) => r.workspace!.id)
      .sort()
      .join(',');
  });

  useEffect(() => {
    if (!storesWorkspaceIds) return;
    storesWorkspaceIds.split(',').forEach((id) => {
      dispatch(
        StoerkIdAPI.endpoints.getWorkspaceStoresUsage.initiate({
          workspaceId: id,
          includeChildWorkspaces: true,
          includeStores: false,
        })
      );
    });
  }, [dispatch, storesWorkspaceIds]);

  // Dispatch user queries using stable ID keys
  useEffect(() => {
    if (!userIdsToFetch) return;
    userIdsToFetch.split(',').forEach((id) => {
      dispatch(UserAPI.endpoints.getUser.initiate(id));
    });
  }, [dispatch, userIdsToFetch]);
  /** filters */
  const [valueTab, setValueTab] =
    React.useState<CustomersFiltersProps['valueTab']>('ALL');
  const [search, setSearch] = React.useState<string>('');
  const handleChangeTabFilter = (
    event: React.SyntheticEvent,
    newValue: CustomersFiltersProps['valueTab']
  ) => {
    setValueTab(newValue);
  };
  const handleChangeSearchFilter = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSearch(event.target.value);
  };
  /* filter by text and by tab */
  // Build a dictionary of child count per workspace
  // Uses childIds from workspace when available, falls back to descendants API
  const childCountDicSerialized = useAppSelector((state) => {
    const dic: Record<string, number | undefined> = {};
    groupedRequestByWorkspace?.forEach((request) => {
      const workspace = request.workspace;
      if (!workspace?.id) return;
      const childIds = workspace.childIds;
      if (childIds !== null && childIds !== undefined) {
        dic[workspace.id] = childIds.length;
      } else {
        const descendants =
          StoerkIdAPI.endpoints.getWorkspaceDescendants.select(workspace.id)(
            state
          ).data;
        dic[workspace.id] = descendants ? descendants.length : undefined;
      }
    });
    return JSON.stringify(dic);
  });
  const childCountDic: Record<string, number | undefined> = useMemo(
    () => JSON.parse(childCountDicSerialized),
    [childCountDicSerialized]
  );

  const itemsSorted = React.useMemo(() => {
    if (!groupedRequestByWorkspace) return [];
    return groupedRequestByWorkspace
      .filter((request) => {
        if (search === '') return true;

        const searchLowerCase = search.toLowerCase();
        const textFromRequest = getAllTextFromRequest(request).toLowerCase();
        return textFromRequest.includes(searchLowerCase);
      })
      .filter((request) => {
        switch (valueTab) {
          case 'ALL':
            return true;
          case 'ORGANIZATION': {
            const count = childCountDic[request.workspace?.id ?? ''];
            return count !== undefined && count > 0;
          }
          case 'SIMPLE': {
            const count = childCountDic[request.workspace?.id ?? ''];
            return count === undefined || count === 0;
          }
          case 'IN_PROGRESS':
            return [
              'PAYMENT_FAILED',
              'PENDING_STAFF',
              'PENDING_PAYMENT',
              'PAYMENT_FAILED',
              'USER_ACCEPT',
            ].includes(request.lastRequest?.requestStatus as any);
          case 'COMPLETED':
            return request.lastRequest?.requestStatus === 'ACTIVE';
          case 'CANCELLED':
            return ['REJECTED', 'CANCELLED', 'USER_ACCEPT'].includes(
              request.lastRequest?.requestStatus as any
            );
          default:
            return true;
        }
      });
  }, [groupedRequestByWorkspace, search, valueTab, childCountDic]);

  return (
    <Container
      maxWidth="xl"
      sx={{
        padding: 3,
        paddingBottom: 2,
      }}
      disableGutters
    >
      <Box mb={1}>
        <Typography variant="h1">
          {polyglot.t('stoerk_id.customer_list.title')}
        </Typography>
        <Typography>{polyglot.t('stoerk_id.customer_list.note')}</Typography>
      </Box>

      {isLoading && (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100px"
        >
          <CircularProgress />
        </Box>
      )}

      {isError && <Alert severity="error">{parseErrorMessage(error)}</Alert>}

      {groupedRequestByWorkspace && !isLoading && !isError && (
        <>
          <Box sx={{ mt: 1 }}>
            <CustomersFilters
              valueTab={valueTab}
              onChangeTab={handleChangeTabFilter}
              onChangeSearch={handleChangeSearchFilter}
            />
          </Box>

          <CustomersTable items={itemsSorted} />
        </>
      )}
    </Container>
  );
}

export default CustomersListPage;
